import PropTypes from 'prop-types';
import { push } from 'connected-react18-router';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Map, List } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { isDeepChanged, getChanged, getIn, toJS } from '../../utils/ChangeSpy';
import { showError } from '../../utils/MessagePopup';
import ContactExecutive from '../../components/Contact/ContactExecutive';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { AddNewIndustryContext } from '../../contexts/AddNewIndustryContext';
import AutoCompleteContainer from '../AutoComplete';
import confirm from '../decorators/confirm';
import stopPropagation from '../../decorators/stopPropagation';
import { dateToString } from '../../utils/dateFormat';
import { usePopupContext } from '../../contexts/PopupContext';
import { useEntityEventContext } from '../../contexts/EntityEventContext';
import {
  loadContactExecutive,
  changeField,
  findUsers,
  clearSuggest,
  triggerAccordion,
  saveContactFund,
  saveContactPosition,
  activateContactFund,
  deleteContactFund,
  deleteContactPosition,
  saveContactExecutive,
  saveContactIndustries,
  addContactIndustry,
  deleteContactIndustry,
  saveContactTags,
  addContactTag,
  deleteContactTag,
  setFieldOnOne,
  findTitles,
  findIndustries,
  findProjects,
  closeValidationError,
  updateIndustryTags,
} from '../../actions/contact/contactExecutive';
import {
  enterContactDealNote,
  leaveContactDealNote,
  enterContactEmail,
  leaveContactEmail,
} from '../../actions/contact/contactEvents';
import { fetchExecutivesStatuses } from '../../actions/statuses';

import config from '../../config';

const qualities = config.values.getIn(['contactExecutive', 'quality']);
const interests = config.values.getIn(['contactExecutive', 'interest']);
const relations = config.values.getIn(['contactExecutive', 'relation']);
const qualitiesOfExecutive = config.values.getIn(['contactExecutive', 'qualitiesOfExecutive']);

const getSuggestionValue = suggestion => suggestion?.value ?? '';

const getSuggestIdName = name => {
  switch (name) {
    case 'suggestDirector':
      return 'recordOwnerId';

    case 'suggestAnalyst':
      return 'recordSubOwnerId';

    case 'suggestDealmaker':
      return 'dealmakerId';

    case 'suggestResearcher':
      return 'lastResearcherId';

    default:
      return undefined;
  }
};

const highestFundIndex = contact => {
  let index = 0;
  let max = 0;

  contact.get('funds').forEach((fund, fundIndex) => {
    const value = getIn(fund, 'highStatus');

    if (max < value) {
      max = value;
      index = fundIndex;
    }
  });

  return index;
};

const handleSubmitDetails = event => event.preventDefault();
const handleSubmit = event => event.preventDefault();
const handleSubmitFund = event => event.preventDefault();
const handleSubmitPosition = event => event.preventDefault();

const ContactExecutiveContainer = props => {
  const {
    changeField,
    contact,
    dealNotes,
    emails,
    fetchExecutivesStatuses,
    interests,
    isExecutive,
    loadContactExecutive,
    loading,
    match,
    qualities,
    qualitiesOfExecutive,
    relations,
    statuses,
    findUsers,
    triggerAccordion,
    saveContactFund,
    saveContactPosition,
    activateContactFund,
    deleteContactFund,
    deleteContactPosition,
    saveContactExecutive,
    saveContactIndustries,
    deleteContactIndustry,
    saveContactTags,
    addContactTag,
    deleteContactTag,
    setFieldOnOne,
    findTitles,
    findIndustries,
    findProjects,
    closeValidationError,
    updateIndustryTags,
    push,
    enterContactDealNote,
    leaveContactDealNote,
    inputErrors,
  } = props;

  const popupContext = usePopupContext();
  const { openPopup, closePopup, hasPopup, onClosePopup } = popupContext;
  const { onEditEvent, addOnSaveCallback, removeOnSaveCallback } = useEntityEventContext();

  const liveRef = useRef();

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const checkIfExec = () => {
    if (hasPopup()) return;
    if (loading) return;

    const { contactId } = match.params;
    let name;

    if (!isExecutive) {
      if (!isNaN(contactId)) name = 'ExecutiveRolePopup';
      else name = 'AddExecutiveContactPopup';
      openPopup(name, { contactId });
      onClosePopup(() => {
        const { contactId: currentContactId } = liveRef.current.match.params;

        if (!liveRef.current.isExecutive) {
          return liveRef.current.push(`/contact/${currentContactId}`);
        }

        return liveRef.current.loadContactExecutive({ id: currentContactId });
      });
    } else {
      loadContactExecutive({ id: contactId });
    }
  };

  const handleBlurDetails = useCallback(() => {
    const { contactId } = match.params;

    if (contact.get('isValid')) {
      const body = getChanged(contact);

      if (Object.keys(body).length !== 0) {
        body.contactId = contactId;
        Object.keys(body).forEach(key => {
          body[key] = dateToString(body[key]);
        });
        saveContactExecutive(body);
      }
    }

    if (isDeepChanged(contact.get('harvcoTags'))) {
      const harvcoTags = contact.get('harvcoTags').map(harvcoTag => {
        const id = harvcoTag.get('id');
        const tag = getIn(harvcoTag, 'tag');

        return { id, tag };
      });

      saveContactTags({ contactId, body: harvcoTags.toJS() });
    }
  }, [match, contact, saveContactExecutive, saveContactTags]);

  const handleBlurFund = useCallback(() => {
    if (contact.get('isValid')) {
      const fundsToSave = contact.get('funds').filter(fund => isDeepChanged(fund));

      fundsToSave.forEach(fund => {
        const body = getChanged(fund);

        body.id = fund.get('id');
        body.contactId = match.params.contactId;
        saveContactFund(body);
      });
    }
  }, [contact, match, saveContactFund]);

  const handleBlurPosition = useCallback(() => {
    if (contact.get('isValid')) {
      const positionsToSave = contact.get('positions').filter(position => isDeepChanged(position));

      positionsToSave.forEach(position => {
        const body = getChanged(position);

        body.id = position.get('id');
        body.contactId = match.params.contactId;
        if ('startYear' in body) {
          body.startYear = parseInt(body.startYear, 10) || null;
        }
        saveContactPosition(body);
      });
    }
  }, [contact, match, saveContactPosition]);

  const handleBlur = useCallback(() => {
    liveRef.current.handleBlurDetails();
    liveRef.current.handleBlurFund();
    liveRef.current.handleBlurPosition();
  }, []);

  const saveIndustries = useCallback(() => {
    const { contactId } = liveRef.current.match.params;
    const bic = getIn(liveRef.current.contact, 'selectedBICs');
    const bm = getIn(liveRef.current.contact, 'selectedBMs');

    if (!bm.length || !bic.length) {
      showError(liveRef.current.openPopup, ['Industry tags or Business models are missing']);
    } else {
      liveRef.current.saveContactIndustries(contactId, bic, bm);
    }
  }, []);

  liveRef.current = {
    match,
    isExecutive,
    push,
    loadContactExecutive,
    contact,
    changeField,
    saveContactIndustries,
    openPopup,
    handleBlurDetails,
    handleBlurFund,
    handleBlurPosition,
  };

  useEffect(() => {
    document.body.style.height = '100vh !important';
    document.getElementById('root').style.height = '100vh !important';

    checkIfExec();
    addOnSaveCallback(handleBlur);
    addOnSaveCallback(saveIndustries);
    fetchExecutivesStatuses();

    return () => {
      removeOnSaveCallback(handleBlur);
      removeOnSaveCallback(saveIndustries);
    };
  }, []);

  const hasMountedRef = useRef(false);
  const previousLoadingRef = useRef(loading);

  // Deliberately no dependency array: the original componentDidUpdate re-runs
  // checkIfExec after ANY prop change (PureComponent), as long as the PREVIOUS
  // render's `loading` prop was true - not just when `loading` itself changes.
  useEffect(() => {
    if (hasMountedRef.current) {
      if (previousLoadingRef.current) {
        checkIfExec();
      }
    } else {
      hasMountedRef.current = true;
    }
    previousLoadingRef.current = loading;
  });

  const addNewIndustry = useCallback(() => {
    openPopup('NewIndustryPopup', {
      afterClose: value => {
        const index = liveRef.current.contact
          .getIn(['contactIndustries'])
          .findIndex(industry => industry.get('isEditing'));

        liveRef.current.changeField({
          name: `contactIndustries.${index}.industryLabel`,
          value: value.label,
        });
        liveRef.current.changeField({
          name: `contactIndustries.${index}.pivot.industryId`,
          value: value.id,
        });
      },
    });
  }, [openPopup]);

  const addNewIndustryContextValue = useMemo(() => ({ addNewIndustry }), [addNewIndustry]);

  const addIndustry = useCallback(() => {
    const tags = getIn(contact, 'selectedBICs');
    const models = getIn(contact, 'selectedBMs');

    openPopup('TagsManagementPopup', {
      addNew: false,
      clientView: true,
      selectedTags: tags.reduce((accumulator, tag) => {
        accumulator[tag] = true;

        return accumulator;
      }, {}),
      selectedModels: models.reduce((accumulator, model) => {
        accumulator[model] = true;

        return accumulator;
      }, {}),
      callback: (tagSelecteds, modelSelecteds) => updateIndustryTags(tagSelecteds, modelSelecteds),
    });
  }, [contact, openPopup, updateIndustryTags]);

  const deleteIndustryAfterConfirm = useCallback(
    (event, data) => {
      const { contactId } = match.params;
      const industryId = data.id;
      const industries = contact.get('contactIndustries');
      const body = toJS(industries)
        .filter(industry => industry.id !== industryId)
        .map((industry, index) => {
          industry.pivot.priority = index;

          return industry.pivot;
        });

      deleteContactIndustry({ contactId, industryId, body }, closePopup);
    },
    [match, contact, deleteContactIndustry, closePopup],
  );

  const deleteIndustry = useCallback(stopPropagation(confirm('Delete this industry?', deleteIndustryAfterConfirm, popupContext)), [
    deleteIndustryAfterConfirm,
    popupContext,
  ]);

  const handleClickIndustries = useCallback(
    (event, index) => {
      const field = 'contactIndustries';
      const name = 'isEditing';
      const value = true;
      const other = false;

      setFieldOnOne({ field, name, value, other, index });
    },
    [setFieldOnOne],
  );

  const handleClickTags = useCallback(
    (event, index) => {
      const field = 'harvcoTags';
      const name = 'isEditing';
      const value = true;
      const other = false;

      setFieldOnOne({ field, name, value, other, index });
    },
    [setFieldOnOne],
  );

  const addTag = useCallback(() => {
    addContactTag();
  }, [addContactTag]);

  const deleteTagAfterConfirm = useCallback(
    (event, data) => {
      const { contactId } = match.params;
      const tagId = data.id;

      deleteContactTag({ contactId, tagId }, closePopup);
    },
    [match, deleteContactTag, closePopup],
  );

  const deleteTag = useCallback(stopPropagation(confirm('Delete this tag?', deleteTagAfterConfirm, popupContext)), [
    deleteTagAfterConfirm,
    popupContext,
  ]);

  const handleDialNotesMouseEnter = useCallback(
    (event, eventId) => {
      enterContactDealNote({ eventId });
    },
    [enterContactDealNote],
  );

  const handleDialNotesMouseLeave = useCallback(
    (event, eventId) => {
      leaveContactDealNote({ eventId });
    },
    [leaveContactDealNote],
  );

  // NOTE: preserved from the original class - these "email" handlers call the deal-note
  // actions too, not `enterContactEmail`/`leaveContactEmail` (imported but never used).
  const handleEmailMouseEnter = useCallback(
    (event, eventId) => {
      enterContactDealNote({ eventId });
    },
    [enterContactDealNote],
  );

  const handleEmailMouseLeave = useCallback(
    (event, eventId) => {
      leaveContactDealNote({ eventId });
    },
    [leaveContactDealNote],
  );

  const handleChange = useCallback(
    event => {
      const { name, value: rawValue, checked, type } = event.target;
      const value = type === 'checkbox' ? checked : rawValue;

      return changeField({ name, value });
    },
    [changeField],
  );

  const addFund = useCallback(() => {
    const name = 'AddFundPopup';
    const { contactId } = match.params;

    openPopup(name, { contactId, statuses });
  }, [match, statuses, openPopup]);

  const deleteFundAfterConfirm = useCallback(
    (event, data) => {
      const { contactId } = match.params;
      const { id } = data;

      deleteContactFund({ contactId, id }, closePopup);
    },
    [match, deleteContactFund, closePopup],
  );

  const deleteFund = useCallback(stopPropagation(confirm('Delete this fund?', deleteFundAfterConfirm, popupContext)), [
    deleteFundAfterConfirm,
    popupContext,
  ]);

  const triggerFund = useCallback(
    (event, index) => {
      triggerAccordion({ field: 'funds', index });
    },
    [triggerAccordion],
  );

  const activateFund = useCallback(
    (event, index) => {
      activateContactFund({ index });
    },
    [activateContactFund],
  );

  const addPosition = useCallback(() => {
    const name = 'AddPositionPopup';
    const { contactId } = match.params;

    openPopup(name, {
      contactId,
      cb: addPosition,
    });
  }, [match, openPopup]);

  const triggerPosition = useCallback(
    (event, index) => {
      triggerAccordion({ field: 'positions', index });
    },
    [triggerAccordion],
  );

  const deletePositionAfterConfirm = useCallback(
    (event, data) => {
      const { contactId } = match.params;
      const { id } = data;

      deleteContactPosition({ contactId, id }, closePopup);
    },
    [match, deleteContactPosition, closePopup],
  );

  const deletePosition = useCallback(stopPropagation(confirm('Delete this position?', deletePositionAfterConfirm, popupContext)), [
    deletePositionAfterConfirm,
    popupContext,
  ]);

  const handleFetchIndustries = useCallback(
    ({ value }) => {
      findIndustries({ filter: value });
    },
    [findIndustries],
  );

  const handleSelectIndustry = useCallback(
    (name, event, { suggestion }) => {
      changeField({ name, value: suggestion.text });

      const idName = name.replace(/industryLabel$/, 'pivot.industryId');

      changeField({ name: idName, value: suggestion.id });
    },
    [changeField],
  );

  const handleFetchTitles = useCallback(
    data => {
      const { id, value } = data;

      findTitles({ filter: value, positionId: id });
    },
    [findTitles],
  );

  const handleSelectTitle = useCallback(
    (name, event, { suggestion }) => {
      changeField({ name, value: suggestion.text });
    },
    [changeField],
  );

  const handleFetchProjects = useCallback(
    ({ fundId, value }) => {
      findProjects({ fundId, filter: value });
    },
    [findProjects],
  );

  const handleSelectProject = useCallback(
    (name, event, { suggestion } = {}) => {
      if (!suggestion) return;
      changeField({ name, value: suggestion.text });

      const idName = name.split('.');

      idName.pop();
      idName.push('eprojectId');
      changeField({ name: idName, value: suggestion.id });
    },
    [changeField],
  );

  const handleUpdateSuggestions = useCallback(
    (suggestionName, param) => {
      if ('text' in param) {
        const name = suggestionName;
        const value = param.text;

        changeField({ name, value });
      }

      if ('suggestions' in param) {
        const name = 'suggests';
        const value = param.suggestions;

        changeField({ name, value });
      }
    },
    [changeField],
  );

  const suggestIndustry = useMemo(
    () => ({
      onFetch: handleFetchIndustries,
      onUpdate: handleUpdateSuggestions,
      getValue: getSuggestionValue,
      onSelect: handleSelectIndustry,
    }),
    [handleFetchIndustries, handleUpdateSuggestions, handleSelectIndustry],
  );

  const suggestTitles = useMemo(
    () => ({
      onFetch: handleFetchTitles,
      onUpdate: handleUpdateSuggestions,
      getValue: getSuggestionValue,
      onSelect: handleSelectTitle,
    }),
    [handleFetchTitles, handleUpdateSuggestions, handleSelectTitle],
  );

  const suggestProject = useMemo(
    () => ({
      suggests: contact.get('suggests'),
      onFetch: handleFetchProjects,
      onUpdate: handleUpdateSuggestions,
      getValue: getSuggestionValue,
      onSelect: handleSelectProject,
    }),
    [contact, handleFetchProjects, handleUpdateSuggestions, handleSelectProject],
  );

  const getSuggest = name => (
    <AutoCompleteContainer
      idName={getSuggestIdName(name)}
      onChange={changeField}
      onFind={params => {
        switch (name) {
          case 'suggestDirector':
            findUsers({ ...params, role: config.DIRECTOR });
            break;

          case 'suggestAnalyst':
            findUsers({ ...params, role: config.ANALYST_ASSOCIATE });
            break;

          default:
            findUsers(params);
            break;
        }
      }}
      rootPath={['contact', 'executive']}
      suggestionsName="suggests"
      valueName={name}
    />
  );

  if (loading || !isExecutive) {
    return null;
  }

  const suggestDirector = getSuggest('suggestDirector');
  const suggestAnalyst = getSuggest('suggestAnalyst');
  const suggestDealmaker = getSuggest('suggestDealmaker');
  const suggestResearcher = getSuggest('suggestResearcher');
  const contactLoading = contact.get('loading');
  const highest = highestFundIndex(contact);

  return (
    <AddNewIndustryContext.Provider value={addNewIndustryContextValue}>
      <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
        <ContactExecutive
          activateFund={activateFund}
          addFund={addFund}
          addIndustry={addIndustry}
          addPosition={addPosition}
          addTag={addTag}
          contact={contact}
          dealNotes={dealNotes}
          deleteFund={deleteFund}
          deleteIndustry={deleteIndustry}
          deletePosition={deletePosition}
          deleteTag={deleteTag}
          emails={emails}
          highestFundIndex={highest}
          interests={interests}
          isExecutive={isExecutive}
          loading={contactLoading}
          onBlur={handleBlur}
          onBlurDetails={handleBlurDetails}
          onBlurFund={handleBlurFund}
          onBlurPosition={handleBlurPosition}
          onChange={handleChange}
          onClickIndustries={handleClickIndustries}
          onClickTags={handleClickTags}
          onDialNotesMouseEnter={handleDialNotesMouseEnter}
          onDialNotesMouseLeave={handleDialNotesMouseLeave}
          onEditEvent={onEditEvent}
          onEmailMouseEnter={handleEmailMouseEnter}
          onEmailMouseLeave={handleEmailMouseLeave}
          onSubmit={handleSubmit}
          onSubmitDetails={handleSubmitDetails}
          onSubmitFund={handleSubmitFund}
          onSubmitPosition={handleSubmitPosition}
          qualities={qualities}
          qualitiesOfExecutive={qualitiesOfExecutive}
          relations={relations}
          statuses={statuses}
          suggestAnalyst={suggestAnalyst}
          suggestDealmaker={suggestDealmaker}
          suggestDirector={suggestDirector}
          suggestIndustry={suggestIndustry}
          suggestProject={suggestProject}
          suggestResearcher={suggestResearcher}
          suggestTitles={suggestTitles}
          triggerFund={triggerFund}
          triggerPosition={triggerPosition}
        />
      </PrettyErrorProvider>
    </AddNewIndustryContext.Provider>
  );
};

function mapStateToProps(state) {
  return {
    inputErrors: state.contact.executive.get('inputErrors'),
    emails: state.contact.events.get('emails'),
    dealNotes: state.contact.events.get('dealNotes'),
    loading: state.contact.info.get('loading'),
    isExecutive: state.contact.info.get('isExecutive'),
    contact: state.contact.executive,
    loadingTitle: state.contact.executive.getIn(['newPosition', 'titleLoading'], false),
    statuses: state.statuses.get('executiveStatuses'),
    qualities,
    interests,
    relations,
    qualitiesOfExecutive,
  };
}

ContactExecutiveContainer.propTypes = {
  changeField: PropTypes.func.isRequired,
  contact: PropTypes.instanceOf(Map).isRequired,
  dealNotes: PropTypes.instanceOf(List).isRequired,
  emails: PropTypes.instanceOf(List).isRequired,
  fetchExecutivesStatuses: PropTypes.func.isRequired,
  interests: PropTypes.instanceOf(List).isRequired,
  loadContactExecutive: PropTypes.func.isRequired,
  qualities: PropTypes.instanceOf(List).isRequired,
  qualitiesOfExecutive: PropTypes.instanceOf(List).isRequired,
  relations: PropTypes.instanceOf(List).isRequired,
  statuses: PropTypes.instanceOf(Object).isRequired,
};

export { ContactExecutiveContainer };
export default connect(
  mapStateToProps,
  {
    loadContactExecutive,
    changeField,
    findUsers,
    clearSuggest,
    enterContactDealNote,
    leaveContactDealNote,
    enterContactEmail,
    leaveContactEmail,
    triggerAccordion,
    deleteContactFund,
    activateContactFund,
    saveContactFund,
    saveContactPosition,
    saveContactExecutive,
    saveContactIndustries,
    addContactIndustry,
    deleteContactIndustry,
    saveContactTags,
    addContactTag,
    deleteContactTag,
    deleteContactPosition,
    setFieldOnOne,
    findTitles,
    findIndustries,
    findProjects,
    closeValidationError,
    updateIndustryTags,
    push,
    fetchExecutivesStatuses,
  },
  mergeProps,
  connectOptions,
)(memo(ContactExecutiveContainer));
